
import { defineComponent, ref, onMounted, onUnmounted } from "vue";
import { Actions } from "@/store/enums/StoreEnums";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { BALANCE_LABEL, POINT_LABEL } from "@/core/config/labels";
import type { ElForm } from "element-plus";

type FormInstance = InstanceType<typeof ElForm>;

export default defineComponent({
  components: {
  },
  setup() {
    onMounted(() => {
      setCurrentPageBreadcrumbs("数据统计", ["数据统计"])
    })

    const formRef = ref<FormInstance>();
    const store = useStore()
    const route = useRoute()
    const router = useRouter()

    const summary = ref({})
    const monthlySummary = ref<any>({ profit_sum: 0, total_cost: 0, net_profit: 0 })
    const autoRefreshInterval = ref(0)
    const isLoading = ref(false)
    const lastRefreshTime = ref('')
    let refreshTimer: number | null = null

    // Color class helper for positive/negative/zero values
    const valueColorClass = (value: number | string | undefined) => {
      if (value === undefined || value === null) return 'text-gray-500'
      const numValue = typeof value === 'string' ? parseFloat(value) : value
      if (isNaN(numValue) || numValue === 0) return 'text-gray-500'
      return numValue > 0 ? 'text-success' : 'text-danger'
    }

    // 本月真实净利润 = 月毛利 + 月运营成本
    const formatMoney = (n: any) => {
      const num = Number(n || 0)
      const sign = num < 0 ? '-' : ''
      const abs = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return `${sign}¥${abs}`
    }
    const fetchMonthlySummary = () => {
      const d = new Date()
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const last = new Date(y, d.getMonth() + 1, 0).getDate()
      const from = `${y}-${m}-01`
      const to = `${y}-${m}-${String(last).padStart(2, '0')}`
      store
        .dispatch(Actions.GET_OPERATING_COST_MONTHLY_SUMMARY, { from, to })
        .then(() => {
          monthlySummary.value = store.getters.currentOperatingCostMonthlySummary
        })
        .catch(() => { /* silent */ })
    }
    fetchMonthlySummary()

    // Format current time
    const formatTime = () => {
      const now = new Date()
      return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`
    }

    // Refresh dashboard data
    const refreshData = () => {
      isLoading.value = true
      store
        .dispatch(Actions.GET_SUMMARY)
        .then(() => {
          summary.value = store.getters.currentSummary
          lastRefreshTime.value = formatTime()
        })
        .catch(() => {
          // Handle error silently
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    // Handle auto-refresh interval change
    const handleAutoRefreshChange = (interval: number) => {
      // Clear existing timer
      if (refreshTimer) {
        clearInterval(refreshTimer)
        refreshTimer = null
      }

      // Set new timer if interval > 0
      if (interval > 0) {
        refreshTimer = window.setInterval(() => {
          refreshData()
        }, interval * 1000)
      }
    }

    store
      .dispatch(Actions.GET_SUMMARY)
      .then(() => {
        summary.value = store.getters.currentSummary
        lastRefreshTime.value = formatTime()
      })
      .catch(() => {
        // Handle error silently
      })

    // Cleanup timer on unmount
    onUnmounted(() => {
      if (refreshTimer) {
        clearInterval(refreshTimer)
        refreshTimer = null
      }
    })

    const water_list = ref({})

    const water_submit = () => {
      let params = {
        from: `${water_duration.value[0].toLocaleDateString()} ${water_duration.value[0].getHours()}:${water_duration.value[0].getMinutes()}:${water_duration.value[0].getSeconds()}`,
        to: `${water_duration.value[1].toLocaleDateString()} ${water_duration.value[1].getHours()}:${water_duration.value[1].getMinutes()}:${water_duration.value[1].getSeconds()}`,
      }

      store
        .dispatch(Actions.GET_WATER_LIST, params)
        .then(() => {
          water_list.value = store.getters.currentWaterList
        })
        .catch(() => {
          // const [error] = Object.keys(store.getters.getErrors);
          // Swal.fire({
          //   text: store.getters.getErrors[error],
          //   icon: "error",
          //   buttonsStyling: false,
          //   confirmButtonText: "Try again!",
          //   customClass: {
          //     confirmButton: "btn fw-bold btn-light-danger",
          //   },
          // })
        })
    }

    const loser_list = ref({})

    const loser_submit = () => {
      let params = {
        from: `${loser_duration.value[0].toLocaleDateString()} ${loser_duration.value[0].getHours()}:${loser_duration.value[0].getMinutes()}:${loser_duration.value[0].getSeconds()}`,
        to: `${loser_duration.value[1].toLocaleDateString()} ${loser_duration.value[1].getHours()}:${loser_duration.value[1].getMinutes()}:${loser_duration.value[1].getSeconds()}`,
      }

      store
        .dispatch(Actions.GET_LOSER_LIST, params)
        .then(() => {
          loser_list.value = store.getters.currentLoserList
        })
        .catch(() => {
          // const [error] = Object.keys(store.getters.getErrors);
          // Swal.fire({
          //   text: store.getters.getErrors[error],
          //   icon: "error",
          //   buttonsStyling: false,
          //   confirmButtonText: "Try again!",
          //   customClass: {
          //     confirmButton: "btn fw-bold btn-light-danger",
          //   },
          // })
        })
    }

    let date = new Date()
    let from = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
    let to = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59)

    const water_duration = ref([
      from,
      to,
    ])

    const loser_duration = ref([
      from,
      to,
    ])

    water_submit()
    loser_submit()

    const balanceLabel = BALANCE_LABEL
    const pointLabel = POINT_LABEL

    return {
      balanceLabel,
      pointLabel,
      summary,
      water_list,
      water_submit,
      water_duration,
      loser_list,
      loser_submit,
      loser_duration,
      autoRefreshInterval,
      isLoading,
      lastRefreshTime,
      valueColorClass,
      refreshData,
      handleAutoRefreshChange,
      monthlySummary,
      formatMoney,
    }
  },
})
